import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Mail } from 'lucide-react';
import { api } from '@/api/client';

interface Smtp {
  host?: string;
  port?: number;
  secure?: boolean;
  user?: string;
  password?: string;
  fromEmail?: string;
  fromName?: string;
}

export function SmtpSettingsPanel() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['settings', 'smtp'],
    queryFn: async () => {
      try { return (await api.get<Smtp>('/settings/smtp')).data ?? {}; }
      catch { return {} as Smtp; }
    },
  });

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<Smtp>({ defaultValues: data ?? {} });

  useEffect(() => { if (data) reset(data); }, [data, reset]);

  const save = useMutation({
    mutationFn: async (d: Smtp) => {
      const payload: any = { ...d };
      if (payload.port) payload.port = Number(payload.port);
      return (await api.put('/settings/smtp', payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'smtp'] }); toast.success('SMTP settings saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5 max-w-2xl">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        <strong>SMTP / Email gateway.</strong> Used for transactional notifications: order placed, AWB generated,
        invoice ready, password reset, low wallet balance, etc. Common providers: AWS SES (port 587),
        Gmail (smtp.gmail.com:465 with app password), SendGrid (smtp.sendgrid.net:587 user=apikey).
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="SMTP Host *" error={errors.host?.message}>
          <input {...register('host', { required: 'Required' })} placeholder="smtp.sendgrid.net" className="input" />
        </Field>
        <Field label="Port *" error={errors.port?.message}>
          <input type="number" {...register('port', { required: 'Required', valueAsNumber: true, min: 1, max: 65535 })} placeholder="587" className="input" />
        </Field>

        <Field label="Username">
          <input {...register('user')} placeholder="apikey / sender email" className="input" />
        </Field>
        <Field label="Password / API Key">
          <input type="password" {...register('password')} placeholder="(masked when saved)" className="input" />
        </Field>

        <Field label="From Email *" error={errors.fromEmail?.message}>
          <input type="email" {...register('fromEmail', { required: 'Required' })} placeholder="noreply@yourdomain.com" className="input" />
        </Field>
        <Field label="From Name">
          <input {...register('fromName')} placeholder="Courier Platform" className="input" />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register('secure')} className="rounded" />
        Use TLS/SSL (recommended for port 465; 587 uses STARTTLS automatically)
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="button" onClick={() => reset(data ?? {})} disabled={!isDirty} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Reset</button>
        <button type="submit" disabled={save.isPending || !isDirty}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save SMTP Settings
        </button>
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-1">
        <Mail size={12} /> Test-send and full email queue support coming in Phase 14.
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
